import React, { createElement, forwardRef, HTMLAttributes } from 'react';
import { composeBemClasses } from '../composeBemClasses';
import './Typography.css';

export type TypographyVariant = 
    | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
    | 'body1' | 'body2' | 'caption' | 'overline'
    | 'subtitle1' | 'subtitle2';

export type TypographyAlign = 'left' | 'center' | 'right' | 'justify';
export type TypographyColor = 'primary' | 'secondary' | 'accent' | 'error' | 'warning' | 'success';

export interface TypographyProps extends HTMLAttributes<HTMLElement> {
    variant?: TypographyVariant;
    component?: keyof React.JSX.IntrinsicElements;
    align?: TypographyAlign;
    color?: TypographyColor;
    noWrap?: boolean;
    gutterBottom?: boolean;
    children: React.ReactNode;
}

const variantMapping: Record<TypographyVariant, keyof React.JSX.IntrinsicElements> = {
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    subtitle1: 'h6',
    subtitle2: 'h6',
    body1: 'p',
    body2: 'p',
    caption: 'span',
    overline: 'span',
};

const Typography = forwardRef<HTMLElement, TypographyProps>(({
    variant = 'body1',
    component,
    align = 'left',
    color = 'primary',
    noWrap = false,
    gutterBottom = false,
    className = '',
    children,
    ...props
}, ref) => {
    const Component = component || variantMapping[variant] || 'span';
    
    const combinedClassName = composeBemClasses(
        'typography',
        [variant, `align-${align}`, `color-${color}`],
        [noWrap && 'no-wrap', gutterBottom && 'gutter-bottom'],
        className
    );

    return createElement(
        Component,
        {
            ref,
            className: combinedClassName,
            ...props
        },
        children
    );
});

Typography.displayName = 'Typography';

export default Typography;
